import React, { useState, useContext, useEffect } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { toast } from 'react-toastify';
import GenericModal from '../../../components/modal/GenericModal';
import InputText from '../../../components/modalComponents/InputText';
import ModalEnabled from '../../../components/modalComponents/ModalEnabled';
import { AuthContext } from '../../../hooks/context/authContext';
import { useGetRequest } from '../../../hooks/useGetRequest/useGetRequest';
import { useManagePostRequest } from '../../../hooks/useManagePostRequest/useManagePostRequest';
import { responseCode200, mostrarMensajeDeErrorConToast } from '../../../consumers/httpRequiestsUtils';
import { getDetalleForAsignacionTrasladoAPI, desistirAsignacionVacanteAPI, guardarVacanteEnEsperaAPI, confirmarAsignacionAPI } from '../../../consumers/backendApisUrls';
import SeleccionarVacanteModal from './SeleccionarVacanteModal';
import FechaVigenciaEsperaModal from './FechaVigenciaEsperaModal';

// ──────────────────────────────────────────────────────────────────────────
// Modal "Asignación de Vacante - GDH" (08/09/2026, a pedido de Jody) — se abre
// desde el ícono "Asignar vacante" de la grilla de BandejaPostulantes.jsx (ver
// handleAsignarVacante). Corresponde a la etapa "Asignación vacante" del
// catálogo EtapasPostulacion_TYP (ver modelo-datos-notas.md).
//
// Por pedido explícito de Jody, la primera entrega fue SOLO visual: sin
// conexión a backend (ni para leer el detalle del postulante ni para guardar
// la asignación). Los tres botones inferiores siguen sin conectar: solo dejan
// constancia en consola de la data armada hasta el momento (fuera del alcance
// de este pedido).
//
// 11/09/2026: ya conecta a backend para LEER el detalle del postulante —
// GetDetalleForAsignacionTraslado (Backend/Intranet.RestAPI → Java
// obtenerDetalleForAsignacionTraslado, que además valida que el postulante
// haya completado satisfactoriamente las etapas previas — Revisión Inicial,
// Feedback Dirección y, si el proceso es Promoción, Evaluación académica/
// Clase modelo/Entrevista — y que la convocatoria no esté cancelada). Se
// dispara al abrir el modal (useEffect sobre postulante.idPostulante), igual
// que RevisionInicialGDH.jsx/FeedbackDireccion.jsx. Si el backend responde
// con error (400, con el mensaje de la validación que no se cumplió — ver
// ProcesoReadUseCase.obtenerDetalleForAsignacionTraslado, que internamente
// mapea 404/422 de Java a 400 vía GetFromApi/BadRequest), se muestra ese
// mensaje con el mismo toast de error que ya usa el resto del proyecto
// (mostrarMensajeDeErrorConToast, misma utilidad que usePostRequest) y el
// modal se cierra solo, porque sin esas validaciones no tiene sentido dejar
// la ficha abierta.
//
// 15/09/2026: ya conecta a backend para GUARDAR las 3 acciones (Desiste/Apto en
// espera/Confirmar Asignación), vía DesistirAsignacionVacante/GuardarVacanteEnEspera/
// ConfirmarAsignacion (Backend/Intranet.RestAPI → Java desistirasignacionvacante/
// guardarvacanteenespera/confirmarasignacion). Mismo patrón que
// RevisionInicialGDH.jsx (useManagePostRequest, estado isSending, toast.success
// manual, refreshPage + toggleModal al terminar). El concepto de "Responsable de
// Asignación" ya no se envía ni se valida (a pedido de Jody, 14/09/2026): el campo
// sigue en pantalla solo a modo informativo (nombre del usuario logueado), el
// IdUsuario real lo resuelve el Service desde _session.IdUsuario. Confirmar
// Asignación exige, además del postulante: vacante destino seleccionada (con su
// IdSede) y fecha de traslado — se validan en cliente antes de llamar al backend.
//
// 08/09/2026 (ajuste): Jody compartió un boceto a mano alzada del diseño
// exacto que quiere ver — se rehizo el layout para calcarlo (título
// "Asignación de Vacante - GDH", campos Sede de Interés 1/2/3 y Motivo de
// Traslado antes de Vacante Destino, reordenamiento de los campos de Vacante
// Destino en 2 columnas, etiqueta "Responsable de Asignación", y un nuevo
// campo "Comentario"). Notas sobre estos campos nuevos:
//   - "Sede de Interés 1/2/3": en el boceto aparecen con fondo gris (solo
//     lectura). Son los mismos 3 valores que el postulante ya guardó en la
//     Ficha de Postulación (ver TP_SedeInteres / RevisionInicialGDH.jsx, que
//     sí los trae editables vía detalle.SedesInteres) — acá se muestran de
//     solo lectura porque en esta etapa ya no se editan, solo se consultan.
//     14/09/2026: ya traen el dato real (`detalle.SedeInteres1/2/3`, en el
//     orden en que el postulante las registró — ver GetDetalleForAsignacionTraslado).
//   - "Motivo de Traslado": también de solo lectura en el boceto (fondo gris,
//     caja más alta que el resto) — se armó como textarea deshabilitado.
//     11/09/2026: ya trae el dato real (`detalle.MotivoTraslado`, TP_Postulante.
//     IdMotivoTraslado resuelto contra el catálogo, con "Otros: <detalle>"
//     concatenado cuando el motivo es "Otros" — ver GetDetalleForAsignacionTraslado).
//   - "Comentario": a diferencia de todo lo anterior, en el boceto aparece
//     SIN fondo gris — es decir, editable. Se agregó como textarea de texto
//     libre para que GDH deje una nota al confirmar/rechazar la asignación.
//
// Cabecera (foto + Nombre/Cargo/Sede): calcada del layout de
// fichaMisDatos/FichaDetalleEmpleado.js (usado por la pantalla real de
// "fichadedatos/fromGestionFicha/:id" que Jody señaló como ejemplo) — círculo
// de foto, nombre en negrita azul (clase blue_innova_text, ya usada en
// gestionRequerimientos/TableWebBody.jsx) y dos líneas más abajo con Cargo y
// Sede. Antes salía directo de la fila de la grilla (`postulante.nombrePostulante`
// / `desCargo` / `desSede`, ver BandejaPostulantes.jsx); 11/09/2026: ahora usa
// `detalle.NombreEmpleado` / `detalle.Cargo` / `detalle.Sede` (GetDetalleForAsignacionTraslado)
// en cuanto llega la respuesta, con esos mismos valores de la grilla como
// fallback mientras el request está en vuelo (mismo criterio que
// RevisionInicialGDH.jsx). La foto si usa `detalle.UrlFotoPerfil`
// (Empleado.UrlFotoPerfil) — si no llega o el postulante no tiene, se muestra
// el mismo ícono placeholder circular de antes.
//
// "Vacante destino": mismo patrón que NuevaConvocatoriaIndividual.jsx —
// InputText de solo lectura con el Id de la vacante elegida + botones
// "Seleccionar vacante" (abre SeleccionarVacanteModal.jsx, la grilla real
// conectada a RequerimientoPersonal) y "Eliminar vacante". A pedido de Jody,
// además del Id se muestran Cargo/Sede/Jornada/Tipo Vacante/Estado de la
// vacante elegida. Mapeo contra la fila que devuelve SeleccionarVacanteModal:
// Cargo → fila.Cargo, Sede → fila.Sede, Jornada → fila.TipoEmpleado (ya
// rotulado "Jornada Laboral" en esa misma grilla).
//   - "Tipo Vacante" (24/09/2026): Jody agregó el campo "TipoVacante" (string,
//     "Fijo"/"Temporal") a la respuesta del endpoint
//     RequerimientoPersonalIndexForTP (GestionRequerimientoMapper, a partir de
//     RequerimientoPersonal.IdTipoVacante). SeleccionarVacanteModal.jsx no
//     mapea/filtra los campos de `fila` (pasa el objeto completo tal cual llega
//     de la API a `onSeleccionar(fila)`), así que `fila.TipoVacante` ya viaja
//     hasta acá sin necesidad de tocar ese modal — value={vacante?.TipoVacante}
//     abajo queda resuelto automáticamente, sin cambios de código.
//   - Pregunta abierta para Jody — "Estado": se muestra `EstadoProceso`
//     (Abierto/Cerrado) en vez de `EstadoAprobacion`, por parecer el más
//     relevante acá; a confirmar si Jody prefiere el otro.
//
// "Sueldo de Promoción" (24/09/2026, a pedido de Jody): al lado de "Estado".
// Solo se muestra y solo es obligatorio para Confirmar Asignación cuando
// `detalle.TipoProceso === 'P'` (Promoción) — campo nuevo de
// GetDetalleForAsignacionTraslado (GET_DETALLE_POSTULANTE_FOR_ASIGNACION_TRASLADO,
// Sql.java). En Traslado ('T') queda oculto y no se exige. Solo acepta dígitos
// (entero, sin decimales — ej. 1200), mismo idioma que ya usa el resto del
// proyecto para inputs numéricos de solo-dígitos (replace(/\D/g, ''), ver
// ConfirmarBeneficios.jsx/ModalCrearPostulante.jsx). NO se envía todavía en el
// payload de ConfirmarAsignacion — Jody no lo pidió en este cambio; el destino
// más probable en backend es Postulante.NuevoSueldo (TP_Postulante), que ya
// existe con ese propósito, pero falta confirmar el nombre exacto que espera
// el endpoint antes de conectarlo.
//
// "Responsable de Asignación": InputText de solo lectura con el nombre del
// usuario logueado (`state.user.fullName`, AuthContext) — mismo campo que ya
// usan CrearSolicitudVacaciones.js (para Solicitante, `state.user.id_empleado`)
// y NuevaConvocatoriaIndividual.jsx.
//
// "Fecha de traslado": DatePicker de la librería `react-datepicker`, mismo
// patrón que vacaciones/modales/CrearSolicitudVacaciones.js — no el
// components/modalComponents/InputDate.jsx (ese es un <input type="date">
// nativo sin calendario, no lo que pidió Jody).
//
// Colores de los 3 botones inferiores (a pedido explícito de Jody): "Apto en
// espera" azul, igual que el botón "< Atrás" de BandejaPostulantes.jsx
// (clase base "generic-button-buscador", sin modificador de color); "Confirmar
// Asignación" verde ("generic-button-hover-verde green-innova-background",
// igual que "Apto"/"Enviar" en el resto del módulo); "Desiste" rojo
// ("generic-button-hover-rojo", igual que "No apto").
// ──────────────────────────────────────────────────────────────────────────

const AsignarVacante = ({ toggleModal, refreshPage, postulante = {} }) => {
    const { state } = useContext(AuthContext);
    const [executeGet] = useGetRequest();
    const [managePost] = useManagePostRequest();

    const [modalVacante, setModalVacante] = useState({ isEnable: false });
    const [modalFechaVigencia, setModalFechaVigencia] = useState({ isEnable: false });
    const [vacante, setVacante] = useState(null);
    const [fechaTraslado, setFechaTraslado] = useState(null);
    const [comentario, setComentario] = useState("");
    const [detalle, setDetalle] = useState(null);
    const [isSending, setIsSending] = useState(false);
    
    const [sueldoPromocion, setSueldoPromocion] = useState("");
    const esPromocion = detalle?.TipoProceso === 'P';

    const nombreResponsable = state?.user?.fullName ?? '';

    // Trae el detalle real del postulante (foto, nombre, cargo, sede y motivo
    // de traslado) apenas se abre el modal. GetDetalleForAsignacionTraslado
    // valida en el backend que el postulante haya completado satisfactoriamente
    // las etapas previas (Revisión Inicial, Feedback Dirección y, si aplica,
    // Evaluación académica/Clase modelo/Entrevista) y que la convocatoria no
    // esté cancelada; si alguna no se cumple, responde con error (400 con el
    // mensaje de la validación puntual) — se muestra ese mensaje con el mismo
    // toast de error que usa el resto del proyecto y se cierra el modal, ya
    // que sin esas validaciones no corresponde continuar con la asignación.
    useEffect(() => {
        if (!postulante?.idPostulante) return;
        let cancelado = false;

        (async () => {
            const detalleResp = await executeGet(getDetalleForAsignacionTrasladoAPI(postulante.idPostulante));
            if (cancelado) return;

            if (responseCode200(detalleResp)) {
                setDetalle(detalleResp.data);
            } else {
                mostrarMensajeDeErrorConToast(detalleResp);
                toggleModal();
            }
        })();

        return () => { cancelado = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [postulante?.idPostulante]);

    // Mientras el request está en vuelo, se muestran los valores de la fila de
    // la grilla (mismo criterio que RevisionInicialGDH.jsx) para no perder el
    // layout; en cuanto llega `detalle` prevalece el dato real del backend.
    const nombreEmpleado = detalle?.NombreEmpleado ?? postulante.nombrePostulante;
    const desCargo = detalle?.Cargo ?? postulante.desCargo;
    const desSede = detalle?.Sede ?? postulante.desSede;
    const urlFotoPerfil = detalle?.UrlFotoPerfil;
    const motivoTraslado = detalle?.MotivoTraslado ?? '';
    // Hasta 3 sedes de interes registradas por el postulante en su Ficha de
    // Postulación (TP_SedeInteres), de solo lectura acá — ver GetDetalleForAsignacionTraslado.
    const sedeInteres1 = detalle?.SedeInteres1 ?? '';
    const sedeInteres2 = detalle?.SedeInteres2 ?? '';
    const sedeInteres3 = detalle?.SedeInteres3 ?? '';
    
    const idsSedesInteres = [detalle?.IdSedeInteres1, detalle?.IdSedeInteres2, detalle?.IdSedeInteres3]
        .filter(Boolean);

    // 28/09/2026: además de sedes de interés, cuando el proceso es Traslado ('T')
    // la grilla de vacantes debe prefiltrarse también por Cargo — comparando el
    // Empleado.IdCargo del postulante (detalle.IdCargo) contra RequerimientoPersonal.
    // IdCargo. En Promoción ('P') no aplica: se envía null y SeleccionarVacanteModal
    // no filtra por cargo.
    const idCargoPostulante = detalle?.TipoProceso === 'T' ? detalle?.IdCargo : null;

    const handleAbrirSeleccionarVacante = () => {
        setModalVacante({
            isEnable: true,
            component: SeleccionarVacanteModal,
            data: {
                onSeleccionar: (fila) => setVacante(fila),
                sede: null,
                sedes: idsSedesInteres,
                idCargo: idCargoPostulante,
            },
        });
    };

    const handleEliminarVacante = () => {
        setVacante(null);
    };

    const handleFechaChange = (date) => {
        setFechaTraslado(date);
    };

    const handleSueldoPromocionChange = (e) => {
        setSueldoPromocion((e.target.value ?? '').replace(/\D/g, ''));
    };

    
    // AGA-756: en Traslado, "Apto en espera" primero pide la fecha de vigencia
    // (FechaVigenciaEsperaModal); Cancelar no aplica nada. En Promoción se guarda
    // directo, como antes. Este modal solo se abre desde BandejaPostulantes.jsx, que
    // solo existe para convocatorias Masivas (Individual no tiene bandeja), así que
    // basta con mirar TipoProceso. Se espera al detalle para saber el proceso.
    const guardarAptoEnEspera = async (fechaVigencia) => {
        setIsSending(true);
        try {
            const payload = { IdPostulante: postulante.idPostulante, FechaVigencia: fechaVigencia };
            await managePost(guardarVacanteEnEsperaAPI(), payload, async () => {
                toast.success('Postulante marcado como Apto en espera exitosamente');
                if (refreshPage) await refreshPage();
                setModalFechaVigencia({ isEnable: false });
                toggleModal();
            }, false);
        } finally {
            setIsSending(false);
        }
    };

    const handleAptoEnEspera = async () => {
        if (!postulante?.idPostulante || isSending || !detalle) return;
        if (detalle.TipoProceso === 'T') {
            setModalFechaVigencia({
                isEnable: true,
                component: FechaVigenciaEsperaModal,
                data: { onConfirm: guardarAptoEnEspera },
            });
            return;
        }
        await guardarAptoEnEspera(undefined);
    };

    const handleConfirmarAsignacion = async () => {
        if (!postulante?.idPostulante || isSending) return;
        if (!vacante?.IdRequerimientoPersonal) {
            toast.error('Debe seleccionar la vacante destino');
            return;
        }
        if (!vacante?.IdSede) {
            toast.error('La vacante seleccionada no tiene una sede asociada');
            return;
        }
        if (!fechaTraslado) {
            toast.error('Debe indicar la fecha de traslado');
            return;
        }
        if (esPromocion && !sueldoPromocion) {
            toast.error('Debe ingresar el sueldo de promoción');
            return;
        }
        setIsSending(true);
        try {
            const payload = {
                IdPostulante: postulante.idPostulante,
                IdRequerimientoPersonal: vacante.IdRequerimientoPersonal,
                IdSedeTraslado: vacante.IdSede,
                FechaEjecucion: fechaTraslado.toISOString().split('T')[0],
                Comentario: comentario,
                
                IdCargo: vacante.IdCargo,
                NuevoSueldo: esPromocion ? Number(sueldoPromocion) : undefined,
            };
            await managePost(confirmarAsignacionAPI(), payload, async () => {
                toast.success('Asignación de vacante confirmada exitosamente');
                if (refreshPage) await refreshPage();
                toggleModal();
            }, false);
        } finally {
            setIsSending(false);
        }
    };

    const handleDesiste = async () => {
        if (!postulante?.idPostulante || isSending) return;
        setIsSending(true);
        try {
            await managePost(desistirAsignacionVacanteAPI(), { IdPostulante: postulante.idPostulante }, async () => {
                toast.success('Postulante marcado como Desiste exitosamente');
                if (refreshPage) await refreshPage();
                toggleModal();
            }, false);
        } finally {
            setIsSending(false);
        }
    };

    return (
        <GenericModal showModal={toggleModal} titulo="Asignación de Vacante - GDH" width="900px">
            <div className="modal-body pt-3">
                <ModalEnabled modalEnabled={modalVacante} setModalEnabled={setModalVacante} refreshPage={() => { }} />
                <ModalEnabled modalEnabled={modalFechaVigencia} setModalEnabled={setModalFechaVigencia} />

                <div className="d-flex align-items-center mb-3" style={{ gap: '15px' }}>
                    <div style={{
                        borderRadius: '50%',
                        width: '70px',
                        height: '70px',
                        backgroundColor: '#e9ecef',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        overflow: 'hidden',
                    }}>
                        {urlFotoPerfil
                            ? <img src={urlFotoPerfil} alt={nombreEmpleado} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            : <FontAwesomeIcon icon="user" size="2x" style={{ color: '#adb5bd' }} />}
                    </div>
                    <div>
                        <div className="font-gothan-medium blue_innova_text" style={{ fontSize: '1.05em' }}>
                            {nombreEmpleado}
                        </div>
                        <div>{desCargo}</div>
                        <div>{desSede}</div>
                    </div>
                </div>

                <hr />

                <div className="row">
                    <div className="col-sm-6"><InputText label="Sede de Interés 1" name="sedeInteres1" value={sedeInteres1} direction="column" isDisabled /></div>
                    <div className="col-sm-6"><InputText label="Sede de Interés 2" name="sedeInteres2" value={sedeInteres2} direction="column" isDisabled /></div>
                </div>
                <div className="row">
                    <div className="col-sm-6"><InputText label="Sede de Interés 3" name="sedeInteres3" value={sedeInteres3} direction="column" isDisabled /></div>
                </div>

                <hr />

                {!esPromocion &&
                    <div className="mb-2">
                        <label className="modal-label d-flex">Motivo de Traslado</label>
                        <textarea className="form-control" rows={3} disabled value={motivoTraslado} onChange={() => { }} />
                    </div>
                }

                <hr />

                <div className="row">
                    <div className="col-sm-12">
                        <label className="modal-label d-flex">Vacante destino</label>
                        <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                            <div className="black-color" style={{ flex: 1 }}>
                                <input type="text" className="form-control" disabled
                                    placeholder="Ninguna vacante seleccionada"
                                    value={vacante?.IdRequerimientoPersonal ?? ""} />
                            </div>
                            <button type="button" className="btn generic-button-buscador" onClick={handleAbrirSeleccionarVacante}>Seleccionar vacante</button>
                            <button type="button" className="btn generic-button-buscador" onClick={handleEliminarVacante}>Eliminar vacante</button>
                        </div>
                    </div>
                </div>

                <div className="row">
                    <div className="col-sm-6"><InputText label="Cargo" name="cargoVacante" value={vacante?.Cargo} direction="column" isDisabled /></div>
                    <div className="col-sm-6"><InputText label="Sede" name="sedeVacante" value={vacante?.Sede} direction="column" isDisabled /></div>
                </div>
                <div className="row">
                    <div className="col-sm-6"><InputText label="Jornada" name="jornadaVacante" value={vacante?.TipoEmpleado} direction="column" isDisabled /></div>
                    <div className="col-sm-6"><InputText label="Tipo Vacante" name="tipoVacante" value={vacante?.TipoVacante} direction="column" isDisabled /></div>
                </div>
                <div className="row">
                    <div className="col-sm-6"><InputText label="Estado" name="estadoVacante" value={vacante?.EstadoProceso} direction="column" isDisabled /></div>
                    {esPromocion &&
                        <div className="col-sm-6">
                            <InputText label="Sueldo de Promoción" name="sueldoPromocion" direction="column"
                                isRequired value={sueldoPromocion} onChange={handleSueldoPromocionChange}
                                placeholder="Ingresar Sueldo de Promoción" />
                        </div>
                    }
                </div>

                <div className="row mt-2">
                    <div className="col-sm-6">
                        <InputText label="Responsable de Asignación" name="responsable" value={nombreResponsable} direction="column" isDisabled />
                    </div>
                    <div className="col-sm-6">
                        <label className="modal-label d-flex">Fecha de Traslado</label>
                        <DatePicker className="form-control" name="FechaTraslado"
                            selected={fechaTraslado}
                            onChange={handleFechaChange}
                            dateFormat="dd/MM/yyyy"
                            placeholderText="dd/mm/aaaa" />
                    </div>
                </div>

                <div className="mb-2 mt-2">
                    <label className="modal-label d-flex">Comentario</label>
                    <textarea className="form-control" rows={3}
                        value={comentario}
                        onChange={(e) => setComentario(e.target.value)} />
                </div>

                <div className="d-flex justify-content-center mt-4" style={{ gap: '15px' }}>
                    <button type="button" disabled={isSending} className="btn generic-button-buscador" onClick={handleAptoEnEspera}>
                        Apto en espera {isSending && (<FontAwesomeIcon icon='spinner' transform="left-9" className="spinner ml-3" />)}
                    </button>
                    <button type="button" disabled={isSending} className="btn generic-button-buscador generic-button-hover-verde green-innova-background" onClick={handleConfirmarAsignacion}>
                        Confirmar Asignación {isSending && (<FontAwesomeIcon icon='spinner' transform="left-9" className="spinner ml-3" />)}
                    </button>
                    <button type="button" disabled={isSending} className="btn generic-button-buscador generic-button-hover-rojo" onClick={handleDesiste}>
                        Desiste {isSending && (<FontAwesomeIcon icon='spinner' transform="left-9" className="spinner ml-3" />)}
                    </button>
                </div>
            </div>
        </GenericModal>
    )
}

export default AsignarVacante
